import { useQuery } from "@tanstack/react-query";
import { ChartBarIcon, SpinnerGapIcon } from "@phosphor-icons/react";
import { fetchScoreRetrospective } from "../lib/api";

const HORIZONS = [
  { key: "h1", label: "1h" },
  { key: "h24", label: "24h" },
  { key: "d7", label: "7d" },
];

const bucketColor = (b) => (b === "75+" ? "#00E599" : b === "55-74" ? "#6EE7B7" : b === "40-54" ? "#F5A623" : "#FF3366");

const Cell = ({ agg }) => {
  if (!agg || agg.n === 0) {
    return <span className="text-muted-foreground/50 font-mono text-xs">—</span>;
  }
  const up = agg.pctUp >= 50;
  return (
    <div className="flex flex-col items-center">
      <span className="font-mono font-bold text-sm" style={{ color: up ? "#00E599" : "#FF3366" }}>{agg.pctUp}%</span>
      <span className="text-[10px] text-muted-foreground">up · n={agg.n}</span>
      <span className="text-[10px] font-mono text-muted-foreground/80 mt-0.5">
        med {agg.median >= 0 ? "+" : ""}{agg.median}%
      </span>
    </div>
  );
};

export const ScoreRetrospective = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["scoreRetro"],
    queryFn: fetchScoreRetrospective,
    refetchInterval: 300000,
    staleTime: 120000,
  });

  return (
    <section className="space-y-4" data-testid="score-retrospective">
      <div className="flex items-center gap-2">
        <ChartBarIcon size={22} className="text-neon" />
        <h2 className="font-heading font-bold text-xl">Track Record</h2>
      </div>
      <p className="text-sm text-muted-foreground max-w-2xl -mt-1">
        Of the tokens that <span className="text-white">first entered</span> each score bucket, here's what happened to
        price afterward. Winners and losers, no cherry-picking. We only started collecting recently, so samples grow over time.
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground font-mono text-sm py-6">
          <SpinnerGapIcon size={18} className="animate-spin text-neon" /> Loading track record…
        </div>
      ) : (
        <>
          {data?.collecting && (
            <div data-testid="retro-collecting" className="border border-warn/30 bg-warn/[0.06] p-4 text-sm text-muted-foreground leading-relaxed">
              📊 <span className="text-warn font-semibold">Collecting data.</span> We're snapshotting scores every 15 minutes
              ({(data.totalSnapshots || 0).toLocaleString()} snapshots across {(data.tokensTracked || 0).toLocaleString()} tokens so far).
              1-hour outcomes appear within an hour, 24h within a day, and 7d within a week. This table fills in and gets more credible as history builds.
            </div>
          )}

          <div className="border border-white/10 bg-card overflow-hidden">
            <div className="grid grid-cols-4 gap-px bg-white/5 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
              <div className="bg-card px-4 py-2.5">Score bucket</div>
              {HORIZONS.map((h) => <div key={h.key} className="bg-card px-4 py-2.5 text-center">After {h.label}</div>)}
            </div>
            {(data?.buckets || []).map((b) => (
              <div key={b.bucket} className="grid grid-cols-4 gap-px bg-white/5" data-testid={`retro-row-${b.bucket}`}>
                <div className="bg-card px-4 py-4 flex flex-col justify-center">
                  <span className="font-mono font-bold text-base" style={{ color: bucketColor(b.bucket) }}>{b.bucket}</span>
                  <span className="text-[10px] text-muted-foreground">{b.label}</span>
                </div>
                {HORIZONS.map((h) => (
                  <div key={h.key} className="bg-card px-4 py-4 flex items-center justify-center text-center">
                    <Cell agg={b.horizons?.[h.key]} />
                  </div>
                ))}
              </div>
            ))}
          </div>

          <p className="text-[11px] text-muted-foreground/70 leading-relaxed">
            % up = share of tokens whose price was higher at that horizon. med = median return. Measured from the first snapshot a token
            entered the bucket, one sample per token. Past outcomes are evidence, not a prediction — not financial advice.
          </p>
        </>
      )}
    </section>
  );
};
